// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * D3 acceptance criterion 31 -- pattern-group resolver.
 *
 * Pure function that joins caller-supplied `PatternGroup[]` with the
 * visible `ViewElement`s and returns a `ResolvedPatternGroup[]` --
 * one entry per group whose variables resolve to at least one
 * visible variable, each carrying a bounding rectangle in canvas
 * coordinates plus the label.
 *
 * The resolver is the only overlay-side file allowed to import
 * `ViewElement`.  The renderer (`PatternGroupOverlay.tsx`) sees only
 * the resolved geometry and so stays engine-free, keeping the
 * read-only contract trivially verifiable at the file-import level
 * for the renderer.
 *
 * The function is a pure read.  Input arrays are never mutated.
 */

import {
  isNamedViewElement,
  ViewElement,
} from '@simlin/core/datamodel';

import { AuxRadius, ModuleHeight, ModuleWidth, StockHeight, StockWidth } from '../drawing/default';
import { PatternGroup, ResolvedPatternGroup } from './patternGroups';

/**
 * Distance between the variables' outer bounds and the boundary
 * rectangle.  Large enough that variable labels (typically rendered
 * below the variable shape) fit inside the boundary without being
 * clipped by the boundary stroke.
 */
const GROUP_PADDING = 18;

/** Half-width / half-height for the variable's bounding rect. */
function outerHalfBounds(element: ViewElement): { halfW: number; halfH: number } {
  switch (element.type) {
    case 'stock':
      return { halfW: StockWidth / 2, halfH: StockHeight / 2 };
    case 'module':
      return { halfW: ModuleWidth / 2, halfH: ModuleHeight / 2 };
    case 'aux':
    case 'flow':
    case 'alias':
    default:
      return { halfW: AuxRadius, halfH: AuxRadius };
  }
}

/**
 * Resolve pattern-group metadata to render-ready bounding rectangles.
 *
 * For each group, scan the variable idents and find their view
 * elements; compute the axis-aligned bounding rect of all resolved
 * variables (using each element's kind-specific outer bounds) and
 * pad it by `GROUP_PADDING` pixels on all sides.  Groups whose
 * idents do not resolve to any visible variable are dropped
 * silently -- stale metadata should render nothing rather than
 * crash.
 *
 * The returned array preserves the input order so callers can
 * encode z-stacking by ordering their group list (last item paints
 * on top).
 */
export function resolvePatternGroups(
  groups: ReadonlyArray<PatternGroup>,
  elements: ReadonlyArray<ViewElement>,
): ResolvedPatternGroup[] {
  if (groups.length === 0) {
    return [];
  }

  // Build the ident index once; pattern groups typically reference
  // a handful of variables per group, so re-scanning `elements` for
  // each ident would be O(groups * variables * elements).
  const byIdent = new Map<string, ViewElement>();
  for (const el of elements) {
    if (isNamedViewElement(el) && el.ident !== undefined) {
      byIdent.set(el.ident, el);
    }
  }

  const resolved: ResolvedPatternGroup[] = [];
  for (const group of groups) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let count = 0;
    for (const ident of group.variables) {
      const element = byIdent.get(ident);
      if (element === undefined) {
        continue;
      }
      const { halfW, halfH } = outerHalfBounds(element);
      const left = element.x - halfW;
      const top = element.y - halfH;
      const right = element.x + halfW;
      const bottom = element.y + halfH;
      if (left < minX) minX = left;
      if (top < minY) minY = top;
      if (right > maxX) maxX = right;
      if (bottom > maxY) maxY = bottom;
      count++;
    }
    if (count === 0) {
      continue;
    }
    resolved.push({
      uid: group.uid,
      label: group.label,
      x: minX - GROUP_PADDING,
      y: minY - GROUP_PADDING,
      width: maxX - minX + GROUP_PADDING * 2,
      height: maxY - minY + GROUP_PADDING * 2,
      kind: group.kind,
      color: group.color,
    });
  }
  return resolved;
}
